import type { BundleOfferDto, ProductAssetDto } from './bundleOffers.schema';
import type { BundleUIModel } from './bundles.schema';
import type { MarketFiatCurrency } from '@/libs/market/market';

/**
 * Pre-order Shop model. Currency is derived from the authenticated market;
 * it is not part of the bundle-offer API contract.
 */
export type BundleOfferViewModel = BundleUIModel & {
  currency: MarketFiatCurrency;
};

const COVER_APPEARANCE_PRIORITY = ['default'];
const DEFAULT_PROMO_COVER_APPEARANCE = 'default-promo';
const EMPTY_PRODUCT_ASSET: ProductAssetDto = {
  id: '',
  key: null,
  path: '',
  size: 0,
  mimeType: '',
  meta: null,
  createdAt: '',
};

const pickCoverAsset = (assets: ProductAssetDto[], appearances = COVER_APPEARANCE_PRIORITY, fallbackToFirst = true): ProductAssetDto | undefined => {
  if (assets.length === 0) {
    return undefined;
  }

  for (const appearance of appearances) {
    const match = assets.find(asset => asset.appearance === appearance);
    if (match) {
      return match;
    }
  }

  return fallbackToFirst ? assets[0] : undefined;
};

export const pickBundleCoverAsset = (bundleOffer: BundleOfferDto): ProductAssetDto => {
  const defaultPromoAsset = bundleOffer.product.presentation?.isDefaultPromo
    ? pickCoverAsset(bundleOffer.product.assets ?? [], [DEFAULT_PROMO_COVER_APPEARANCE], false)
    : undefined;

  return defaultPromoAsset ?? pickCoverAsset(bundleOffer.offer.assets) ?? EMPTY_PRODUCT_ASSET;
};

/**
 * Maps a personalised bundle offer (GET /users/me/bundle-offers) into the
 * BundleDto shape the Shop modal cards already consume.
 *
 * `id` is the bundle offer id: selecting a bundle creates a deposit with
 * `userBundleOfferId = activeOrder.id`, and the deposit API expects the offer id.
 *
 * The live endpoint splits presentation data across both:
 *   - `offer.presentation`:   isVip, isPromo, dailyBonusUpTo
 *   - `offer.assets`:         fallback card cover artwork
 *   - `product.assets`:       default-promo cover artwork
 *   - `product.presentation`: order (sort), relatedProducts (grouping), isDefaultPromo
 */
export const mapBundleOfferToBundle = (
  bundleOffer: BundleOfferDto,
  currency: MarketFiatCurrency,
): BundleOfferViewModel => {
  const { offer, product } = bundleOffer;
  const offerPresentation = offer.presentation ?? null;
  const productPresentation = product.presentation ?? null;

  return {
    currency,
    id: bundleOffer.id,
    name: offer.name || product.name,
    alias: product.alias ?? undefined,
    priority: productPresentation?.order ?? 0,
    goldCoins: offer.gc,
    sweepCoins: product.sc,
    bonusSweepCoins: offer.bsc,
    price: product.price,
    cover: pickBundleCoverAsset(bundleOffer),
    beforeSalePrice: productPresentation?.beforeSalePrice ?? undefined,
    presentationOfferConfig: offerPresentation,
    isActive: offer.isActive ?? true,
  };
};
